import React, { useState, useEffect } from 'react';
import { 
  Brain, AlertTriangle, CheckCircle, TrendingUp, TrendingDown, Minus,
  Target, Home, Calendar, UserCheck, ChevronDown, ChevronUp, Sparkles,
  Activity, Heart, MessageCircle, Hand
} from 'lucide-react';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const DynamicRecommendations = ({ childId }) => {
  const { t } = useLanguage();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDomains, setExpandedDomains] = useState({});

  useEffect(() => {
    fetchRecommendations();
  }, [childId]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getDynamicRecommendations(childId);
      setRecommendations(res.data);
    } catch (err) {
      console.error('Error fetching dynamic recommendations:', err);
      setError('Failed to load dynamic recommendations');
    } finally {
      setLoading(false);
    }
  };

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'improving':
        return <TrendingUp className="text-green-500" size={20} />;
      case 'declining':
        return <TrendingDown className="text-red-500" size={20} />;
      case 'stable':
        return <Minus className="text-yellow-500" size={20} />;
      default:
        return <Activity className="text-gray-500" size={20} />;
    }
  };

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'improving':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'declining':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'stable':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getDomainIcon = (domain) => {
    switch (domain) {
      case 'motor':
        return <Hand className="text-blue-500" size={18} />;
      case 'cognitive':
        return <Brain className="text-purple-500" size={18} />;
      case 'social_emotional':
        return <Heart className="text-pink-500" size={18} />;
      case 'language':
        return <MessageCircle className="text-teal-500" size={18} />;
      default:
        return <Activity className="text-gray-500" size={18} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track':
        return 'bg-green-100 text-green-700';
      case 'needs_attention':
        return 'bg-yellow-100 text-yellow-700';
      case 'concerning':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <Brain className="animate-pulse text-[#0F4C81]" size={24} />
            <span className="text-[#4B5563]">Generating AI-powered recommendations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border-2 border-red-100 p-6 shadow-sm">
        <div className="flex items-center space-x-3 text-red-600">
          <AlertTriangle size={24} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!recommendations?.has_sufficient_data) {
    return (
      <div className="bg-white rounded-2xl border-2 border-yellow-100 p-6 shadow-sm">
        <div className="flex items-center space-x-3 mb-4">
          <Brain className="text-yellow-500" size={24} />
          <h3 className="text-lg font-bold text-[#111827]">{t('progress.dynamicRecommendations')}</h3>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
          <p className="text-[#92400E]">{recommendations?.message || 'Not enough screening data to generate dynamic recommendations.'}</p>
          <p className="text-sm text-[#B45309] mt-2">Complete at least 2 screenings to unlock AI-powered intervention adjustments.</p>
        </div>
      </div>
    );
  }

  const recs = recommendations?.recommendations || {};

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm" data-testid="dynamic-recommendations">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] p-2 rounded-xl">
            <Sparkles className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#111827]">{t('progress.dynamicRecommendations')}</h3>
            <p className="text-sm text-[#6B7280]">AI-powered intervention adjustments based on progress</p>
          </div>
        </div>
        {recommendations?.ai_generated && (
          <span className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
            <Brain size={14} />
            <span>AI Generated</span>
          </span>
        )}
      </div>

      {/* Overall Assessment */}
      <div className={`rounded-xl p-4 border-2 mb-6 ${getTrendColor(recs.trend_direction)}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold">Overall Trend</span>
          <div className="flex items-center space-x-2">
            {getTrendIcon(recs.trend_direction)}
            <span className="capitalize font-bold">{recs.trend_direction || 'Unknown'}</span>
          </div>
        </div>
        <p className="text-sm">{recs.overall_assessment}</p>
        {recs.confidence_level && (
          <p className="text-xs mt-2 opacity-75">Confidence: {recs.confidence_level}</p>
        )}
      </div>

      {/* Key Observations */}
      {recs.key_observations && recs.key_observations.length > 0 && (
        <div className="mb-6">
          <h4 className="font-bold text-[#111827] mb-3 flex items-center space-x-2">
            <Target size={18} className="text-[#0F4C81]" />
            <span>Key Observations</span>
          </h4>
          <ul className="space-y-2">
            {recs.key_observations.map((obs, idx) => (
              <li key={idx} className="flex items-start space-x-2 text-sm text-[#4B5563]">
                <CheckCircle size={16} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                <span>{obs}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Intervention Adjustments by Domain */}
      {recs.intervention_adjustments && recs.intervention_adjustments.length > 0 && (
        <div className="mb-6">
          <h4 className="font-bold text-[#111827] mb-3 flex items-center space-x-2">
            <Activity size={18} className="text-[#3B82F6]" />
            <span>Intervention Adjustments</span>
          </h4>
          <div className="space-y-3">
            {recs.intervention_adjustments.map((adj, idx) => (
              <div 
                key={idx} 
                className="border-2 border-slate-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleDomain(adj.domain)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {getDomainIcon(adj.domain)}
                    <span className="font-semibold text-[#111827] capitalize">{adj.domain?.replace('_', ' ')}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(adj.current_status)}`}>
                      {adj.current_status?.replace('_', ' ')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityColor(adj.priority)}`}>
                      {adj.priority} priority
                    </span>
                  </div>
                  {expandedDomains[adj.domain] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                
                {expandedDomains[adj.domain] && (
                  <div className="p-4 border-t border-slate-200 bg-slate-50">
                    <p className="text-sm text-[#4B5563] mb-3">{adj.specific_recommendation}</p>
                    
                    {adj.suggested_activities && adj.suggested_activities.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-bold text-[#6B7280] uppercase mb-2">Suggested Activities</p>
                        <ul className="space-y-1">
                          {adj.suggested_activities.map((act, actIdx) => (
                            <li key={actIdx} className="text-sm text-[#4B5563] flex items-center space-x-2">
                              <span className="w-1.5 h-1.5 bg-[#0F4C81] rounded-full"></span>
                              <span>{act}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-4 text-xs text-[#6B7280]">
                      <span>Recommendation: <strong className="text-[#111827]">{adj.recommendation_type?.replace('_', ' ')}</strong></span>
                      <span>Frequency: <strong className="text-[#111827]">{adj.frequency}</strong></span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Caregiver Guidance */}
      {recs.caregiver_guidance && (
        <div className="mb-6">
          <h4 className="font-bold text-[#111827] mb-3 flex items-center space-x-2">
            <Home size={18} className="text-[#F59E0B]" />
            <span>Guidance for Caregivers</span>
          </h4>
          <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-xl p-4 border border-[#FCD34D]">
            {recs.caregiver_guidance.positive_reinforcement && (
              <p className="text-sm text-[#92400E] mb-3 font-medium">
                {recs.caregiver_guidance.positive_reinforcement}
              </p>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recs.caregiver_guidance.areas_of_focus && (
                <div>
                  <p className="text-xs font-bold text-[#78350F] uppercase mb-2">Focus Areas</p>
                  <ul className="space-y-1">
                    {recs.caregiver_guidance.areas_of_focus.map((area, idx) => (
                      <li key={idx} className="text-sm text-[#78350F] flex items-center space-x-2">
                        <Target size={12} />
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {recs.caregiver_guidance.home_activities && (
                <div>
                  <p className="text-xs font-bold text-[#78350F] uppercase mb-2">Home Activities</p>
                  <ul className="space-y-1">
                    {recs.caregiver_guidance.home_activities.map((act, idx) => (
                      <li key={idx} className="text-sm text-[#78350F] flex items-center space-x-2">
                        <CheckCircle size={12} />
                        <span>{act}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {recs.caregiver_guidance.warning_signs && recs.caregiver_guidance.warning_signs.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#F59E0B]/30">
                <p className="text-xs font-bold text-[#B45309] uppercase mb-2 flex items-center space-x-1">
                  <AlertTriangle size={12} />
                  <span>Warning Signs to Watch</span>
                </p>
                <ul className="space-y-1">
                  {recs.caregiver_guidance.warning_signs.map((sign, idx) => (
                    <li key={idx} className="text-sm text-[#B45309]">• {sign}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Follow-up Plan */}
      {recs.follow_up_plan && (
        <div className="bg-[#EFF6FF] rounded-xl p-4 border border-[#BFDBFE]">
          <h4 className="font-bold text-[#1E40AF] mb-3 flex items-center space-x-2">
            <Calendar size={18} />
            <span>Follow-up Plan</span>
          </h4>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-[#6B7280]">Next Screening:</span>
              <span className="ml-2 font-semibold text-[#1E40AF]">
                {recs.follow_up_plan.next_screening_recommended?.replace('_', ' ')}
              </span>
            </div>
            {recs.follow_up_plan.specialist_referral_needed && (
              <div>
                <span className="text-[#6B7280]">Specialist Referral:</span>
                <span className="ml-2 font-semibold text-[#DC2626]">
                  {recs.follow_up_plan.specialist_type?.replace('_', ' ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generated timestamp */}
      {recommendations?.generated_at && (
        <p className="text-xs text-[#9CA3AF] mt-4 text-right">
          Generated: {new Date(recommendations.generated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default DynamicRecommendations;
